import { api } from '../api.js';
import { useCurrentUser, useResource } from '../hooks.js';
import { useApp } from '../pages/AppPage.js';
import { fmtDateTime } from './format.js';
import { ErrorNotice, Loading } from './Notice.js';

const WORDS: Record<string, string> = {
  'app.created': 'App created',
  'app.create_failed': 'Setting up the app failed',
  'app.stopped': 'App stopped',
  'app.started': 'App started',
  'app.deleted': 'App deleted',
  'grant.invited': 'People invited',
  'grant.revoked': 'Access removed',
  'grant.first_access': 'Someone signed in for the first time',
  'env.updated': 'Environment changed',
  'deployment.started': 'Deployment started',
  'deployment.live': 'New version went live',
  'deployment.failed': 'Deployment failed',
};

function describeDetail(detail: Record<string, unknown> | undefined): string {
  if (!detail) return '';
  return Object.entries(detail)
    .map(
      ([k, v]) =>
        `${k}: ${Array.isArray(v) ? v.join(', ') : typeof v === 'object' && v !== null ? JSON.stringify(v) : String(v)}`,
    )
    .join(' · ');
}

export function ActivityTab() {
  const { app } = useApp();
  const me = useCurrentUser();
  const events = useResource(() => api.events(app.slug), [app.slug]);
  const list = events.data ? [...events.data.events].sort((a, b) => (a.at < b.at ? 1 : -1)) : undefined;

  return (
    <section className="card">
      <div className="page-head" style={{ marginBottom: '0.5rem' }}>
        <h2>Activity</h2>
        <button type="button" className="btn btn-sm" onClick={events.reload} disabled={events.loading}>
          Refresh
        </button>
      </div>
      <p className="small subtle">Everything that has happened to this app, newest first.</p>
      <ErrorNotice error={events.error} />
      {events.loading && !list ? <Loading /> : null}
      {list && list.length === 0 ? <p className="subtle">Nothing recorded yet.</p> : null}
      {list && list.length > 0 ? (
        <ul className="rows">
          {list.map((ev) => (
            <li key={`${ev.at}-${ev.action}-${ev.actorSub}`}>
              <div>
                <div className="primary">{WORDS[ev.action] ?? ev.action}</div>
                <div className="secondary">
                  {fmtDateTime(ev.at)} ·{' '}
                  {ev.actorSub === me.sub ? 'you' : ev.actorSub === 'platform' ? 'platform' : 'someone else'}
                  {ev.detail ? ` · ${describeDetail(ev.detail)}` : ''}
                </div>
              </div>
            </li>
          ))}
        </ul>
      ) : null}
    </section>
  );
}
